import { MedicationRenewalControllerService } from "@9amhealth/openapi";
import type {
  MedicationRenewalResponse,
  RenewalRequirementResponse
} from "@9amhealth/openapi";
import type {
  RefillRequirement,
  RefillRequirementsResponse
} from "./RefillRequirementsTypes";

const toRefillRequirement = (
  raw: RenewalRequirementResponse
): RefillRequirement => ({
  type: raw.type,
  label: raw.label,
  fulfilled: raw.fulfilled,
  count: raw.count,
  minCount: raw.minCount,
  expiresAt: raw.expiresAt ?? null,
  state: raw.state
});

/**
 * Adapt the backend `MedicationRenewalResponse` into the app's internal `Refill*` domain
 * shape (exported for tests). This single seam lets the FE keep its `Refill*` vocabulary
 * while the backend speaks "renewal": the nested `requirementsSummary` is flattened, and
 * `category` plus the prescription/purchase identifiers are intentionally not mapped (no UI
 * consumes them). `category` in particular stays unmapped on purpose — the hub decides what is
 * locked from the server's `state`, never from the requirement's clinical lane.
 *
 * Non-concierge renewals have no window, so `renewalWindow` is absent — the window-derived
 * dates fall back to null (the downstream helpers already handle null dates, and the
 * non-concierge card is cohort-driven, not window-driven).
 */
export const toRefillRequirements = (
  raw: MedicationRenewalResponse
): RefillRequirementsResponse => ({
  goalType: raw.type,
  medicationName: raw.medicationName ?? null,
  status: raw.status,
  allFulfilled: raw.requirementsSummary.allFulfilled,
  completedCount: raw.requirementsSummary.completedCount,
  totalCount: raw.requirementsSummary.totalCount,
  windowOpensAt: raw.renewalWindow?.start ?? null,
  dueAt: raw.renewalWindow?.target ?? null,
  windowClosesAt: raw.renewalWindow?.end ?? null,
  nextExpiresAt: raw.requirementsSummary.nextExpiresAt ?? null,
  requirements: raw.requirements.map(toRefillRequirement)
});

/**
 * `GET /v1/medication-renewal/GLP1_WEIGHTLOSS` — the member's active GLP-1 weight-loss
 * renewal, adapted to the FE `RefillRequirementsResponse`. A 404 ("no active renewal") is
 * a normal empty state, handled by the cubit.
 */
export const getRefillRequirements =
  async (): Promise<RefillRequirementsResponse> => {
    const { data } =
      await MedicationRenewalControllerService.getMedicationRenewal(
        "GLP1_WEIGHTLOSS"
      );
    return toRefillRequirements(data);
  };
